import { useCallback, useEffect, useState } from "react";
import { UserCheck, LogOut } from "lucide-react";
import { getImpersonation, endImpersonation } from "@/lib/impersonation";

/**
 * ImpersonationBanner — persistent orange bar at the very top of every
 * app page WHEN the current session was minted via
 * /auth/admin/impersonate/{user_id}. Clicking "Exit" clears the
 * impersonation state, logs out, and returns to /admin/users.
 *
 * The visible "you are impersonating" state lives in localStorage
 * (non-sensitive display metadata only). The actual auth token is a
 * regular session cookie set by the backend.
 */
export default function ImpersonationBanner() {
  const [imp, setImp] = useState(() => getImpersonation());

  // Re-check on route changes and once per minute so the banner
  // disappears the moment the 2h scoped session expires.
  useEffect(() => {
    const tick = () => setImp(getImpersonation());
    const id = setInterval(tick, 60_000);
    window.addEventListener("focus", tick);
    return () => {
      clearInterval(id);
      window.removeEventListener("focus", tick);
    };
  }, []);

  const exit = useCallback(async () => {
    endImpersonation();
    try {
      // Best-effort logout — clears the backend session doc too.
      const { default: api } = await import("@/lib/api");
      await api.post("/auth/logout");
    } catch (err) {
      console.debug("impersonation-exit logout failed", err);
    }
    window.location.href = "/admin/users";
  }, []);

  if (!imp) return null;

  const target = imp.target || {};
  const expiresLabel = imp.expires_at
    ? new Date(imp.expires_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })
    : "—";

  return (
    <div
      data-testid="impersonation-banner"
      className="w-full flex items-center justify-between gap-3 px-4 py-2 text-white text-sm"
      style={{ background: "linear-gradient(90deg, #B23A3A 0%, #E09E50 100%)" }}
    >
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <UserCheck size={15} className="shrink-0" />
        <span className="truncate">
          <span className="opacity-80">You are impersonating</span>{" "}
          <strong className="font-semibold" data-testid="impersonation-target-email">
            {target.email || target.user_id}
          </strong>
          <span className="opacity-70 hidden sm:inline"> · session expires ~{expiresLabel}</span>
        </span>
      </div>
      <button
        onClick={exit}
        data-testid="impersonation-exit-btn"
        className="inline-flex items-center gap-1 px-3 py-1 rounded-md text-xs font-semibold bg-white/20 hover:bg-white/30 transition-colors shrink-0"
      >
        <LogOut size={12} /> Exit
      </button>
    </div>
  );
}
